/**
 * Trial Badge
 *
 * Shows trial status with days remaining.
 */
import { useSubscription } from '@/hooks/useSubscription';

interface TrialBadgeProps {
  className?: string;
}

export function TrialBadge({ className = '' }: TrialBadgeProps) {
  const { isTrialing, trialDaysRemaining, isPremium } = useSubscription();

  // Don't show if not premium
  if (!isPremium) return null;

  // Trial badge
  if (isTrialing && trialDaysRemaining !== null) {
    const isUrgent = trialDaysRemaining <= 2;

    return (
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
        isUrgent
          ? 'bg-orange-100 text-orange-700 border border-orange-200'
          : 'bg-cream-100 text-teal-700 border border-charcoal-200'
      } ${className}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${isUrgent ? 'bg-orange-500' : 'bg-teal-500'}`} />
        {trialDaysRemaining === 0
          ? 'Trial ends today'
          : trialDaysRemaining === 1
            ? '1 day left in trial'
            : `${trialDaysRemaining} days left in trial`
        }
      </div>
    );
  }

  // Active subscription badge (premium but not trialing)
  if (isPremium && !isTrialing) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-cream-100 text-teal-700 border border-charcoal-200 ${className}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
        Premium
      </div>
    );
  }

  return null;
}

export default TrialBadge;